import React, { useState } from 'react';
import { X, AlertTriangle, Send, Bug, Lightbulb, HelpCircle, Mic, Monitor, Loader2 } from 'lucide-react';
import { useTickets } from '../context/TicketContext';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'bug', label: 'Bug / Error', icon: Bug, color: 'text-red-400' },
  { id: 'voice', label: 'Voice / Audio Issue', icon: Mic, color: 'text-blue-400' },
  { id: 'ui', label: 'UI / Display Issue', icon: Monitor, color: 'text-purple-400' },
  { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-amber-400' },
  { id: 'other', label: 'Other / General', icon: HelpCircle, color: 'text-gray-400' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-gray-500' },
  { id: 'medium', label: 'Medium', color: 'bg-amber-500' },
  { id: 'high', label: 'High', color: 'bg-orange-500' },
  { id: 'critical', label: 'Critical', color: 'bg-red-500' },
];

const TicketModal = () => {
  const { showTicketModal, ticketData, closeTicketModal, submitTicket } = useTickets();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [includeErrorDetails, setIncludeErrorDetails] = useState(true);

  if (!showTicketModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || title.length < 5) {
      toast.error('Please provide a title (at least 5 characters)');
      return;
    }
    
    if (!description.trim() || description.length < 10) {
      toast.error('Please describe the issue (at least 10 characters)');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitTicket({
        title,
        description,
        category,
        priority,
        errorMessage: includeErrorDetails ? ticketData.errorMessage : '',
        errorStack: includeErrorDetails ? ticketData.errorStack : '',
        pageUrl: ticketData.pageUrl,
        component: ticketData.component,
      });
      
      toast.success(result.message || `Ticket #${result.ticket_id} created successfully!`);
      
      // Reset form
      setTitle('');
      setDescription('');
      setCategory('bug');
      setPriority('medium');
    } catch (error) {
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      closeTicketModal();
      setTitle('');
      setDescription('');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="relative w-full max-w-lg mx-4 bg-[hsl(240,10%,10%)] rounded-2xl border border-white/10 shadow-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 bg-gradient-to-r from-red-500/20 to-orange-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Report an Issue</h2>
                <p className="text-sm text-gray-400">Help us improve by reporting this problem</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Error Preview (if auto-captured) */}
        {ticketData.errorMessage && (
          <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex items-start gap-2">
              <Bug className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-red-400 mb-1">Captured Error:</p>
                <p className="text-sm text-gray-300 font-mono truncate">{ticketData.errorMessage}</p>
              </div>
            </div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeErrorDetails}
                onChange={(e) => setIncludeErrorDetails(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">Include technical details with ticket</span>
            </label>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue..."
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={200}
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe what happened, what you expected, and steps to reproduce..."
              rows={4}
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              maxLength={5000}
              disabled={isSubmitting}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    disabled={isSubmitting}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                      category === cat.id
                        ? 'bg-blue-500/20 border-blue-500/50 text-white'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    <Icon size={16} className={category === cat.id ? 'text-blue-400' : cat.color} />
                    <span className="text-sm">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
            <div className="flex gap-2">
              {PRIORITIES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPriority(p.id)}
                  disabled={isSubmitting}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                    priority === p.id
                      ? 'bg-white/10 border-white/30 text-white'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${p.color}`} />
                  <span className="text-sm">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Page URL (read-only) */}
          {ticketData.pageUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Page</label>
              <p className="text-sm text-gray-400 truncate">{ticketData.pageUrl}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 bg-white/5 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !title.trim() || !description.trim()}
            className="flex-1 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send size={16} />
                Submit Ticket
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketModal;
